import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, X } from 'lucide-react';
import { api } from '../lib/api';
import { useT } from '../i18n';

export function AdSlot({ placement = 'banner', onClose = null, className = '' }) {
    const [ad, setAd] = useState(null);
    const [platformConfig, setPlatformConfig] = useState(null);
    const t = useT();
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const platform = await api.get('/ads/platform-config', { params: { placement } });
                if (!cancelled) setPlatformConfig(platform.data);
                // Rotation pool: house ads plus user-paid promo ads.
                const pool = [];
                const r = await api.get(`/ads`, { params: { placement } });
                (r.data.items || []).forEach((item) => pool.push({ type: 'house', item }));
                if (placement === 'banner' || placement === 'postroll') {
                    try {
                        const promo = await api.get('/promo/ads/serve', { params: { placement } });
                        if (promo.data?.ad_id) pool.push({ type: 'promo', item: promo.data });
                    } catch {}
                }
                if (!cancelled && pool.length) {
                    const pick = pool[Math.floor(Math.random() * pool.length)];
                    setAd(pick);
                    const prefix = pick.type === 'promo' ? '/promo' : '';
                    api.post(`${prefix}/ads/${pick.item.ad_id}/impression`).catch(() => {});
                }
            } catch {}
        })();
        return () => { cancelled = true; };
    }, [placement]);

    const htmlCodes = platformConfig?.html_codes?.length ? platformConfig.html_codes : (platformConfig?.html_code ? [platformConfig.html_code] : []);
    if (platformConfig?.html_enabled && htmlCodes.length) {
        // Multiple HTML ads rotate: a different one is picked on every mount/reload.
        const chosen = htmlCodes[Math.floor(Math.random() * htmlCodes.length)];
        return <div data-testid="ad-slot-html" className={`relative overflow-hidden rounded-[14px] border border-[color:var(--alb-ad-border)] ${className}`} dangerouslySetInnerHTML={{ __html: chosen }} />;
    }

    if (!ad) return null;

    // User-paid promo ad: links to platform content or an external website.
    if (ad.type === 'promo') {
        const promo = ad.item;
        const isExternal = promo.kind === 'external';
        const handlePromoClick = () => {
            api.post(`/promo/ads/${promo.ad_id}/click`).catch(() => {});
            if (isExternal) window.open(promo.target_url, '_blank', 'noopener,noreferrer');
            else navigate(promo.target_url);
        };
        return (
            <div data-testid="ad-slot-promo" className={`relative overflow-hidden rounded-[14px] border border-[color:var(--alb-ad-border)] bg-[color:var(--alb-ad-bg)] ${className}`}>
                <span className="absolute left-3 top-3 z-10 inline-flex items-center rounded-full bg-[color:var(--alb-ad-badge)] px-2 py-0.5 text-[11px] font-semibold text-[color:var(--alb-ink)]">Promocionat</span>
                {onClose && (
                    <button onClick={onClose} className="absolute right-2 top-2 z-10 grid place-items-center h-7 w-7 rounded-full bg-black/60 text-white"><X className="h-4 w-4" /></button>
                )}
                <button onClick={handlePromoClick} className="relative block w-full text-left z-0">
                    <div className="relative aspect-[5/1] sm:aspect-[6/1] md:aspect-[8/1] w-full overflow-hidden">
                        {(promo.banner || promo.thumbnail) ? <img src={promo.banner || promo.thumbnail} alt={promo.title} className="absolute inset-0 h-full w-full object-cover" /> : <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--alb-surface-2)] to-[color:var(--alb-surface)]" />}
                        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent" />
                        <div className="relative h-full flex flex-col justify-end p-4 md:p-6">
                            <div className="text-white font-display text-lg md:text-2xl font-semibold">{promo.title}</div>
                            <div className="mt-2 inline-flex items-center gap-1 text-[color:var(--alb-sun)] text-sm font-semibold">{isExternal ? 'Visita el lloc' : 'Mira-ho ara'} <ExternalLink className="h-3 w-3" /></div>
                        </div>
                    </div>
                </button>
            </div>
        );
    }

    const adHouse = ad.item || ad;
    const handleClick = () => {
        api.post(`/ads/${adHouse.ad_id}/click`).catch(() => {});
        window.open(adHouse.target_url, '_blank', 'noopener,noreferrer');
    };

    return (
        <div data-testid="ad-slot" className={`relative overflow-hidden rounded-[14px] border border-[color:var(--alb-ad-border)] bg-[color:var(--alb-ad-bg)] alb-ad-stripe ${className}`}>
            <span data-testid="ad-label" className="absolute left-3 top-3 z-10 inline-flex items-center rounded-full bg-[color:var(--alb-ad-badge)] px-2 py-0.5 text-[11px] font-semibold text-[color:var(--alb-ink)]">{t('ad.label')}</span>
            {onClose && (
                <button data-testid="ad-skip-button" onClick={onClose} className="absolute right-2 top-2 z-10 grid place-items-center h-7 w-7 rounded-full bg-black/60 text-white"><X className="h-4 w-4" /></button>
            )}
            <button onClick={handleClick} className="relative block w-full text-left z-0">
                <div className="relative aspect-[5/1] sm:aspect-[6/1] md:aspect-[8/1] w-full overflow-hidden">
                    <img src={adHouse.image_url} alt={adHouse.title} className="absolute inset-0 h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent" />
                    <div className="relative h-full flex flex-col justify-end p-4 md:p-6">
                        <div className="text-white font-display text-lg md:text-2xl font-semibold">{adHouse.title}</div>
                        {adHouse.advertiser && <div className="text-[color:var(--alb-text-muted)] text-xs">{t('ad.sponsored_by', { advertiser: adHouse.advertiser })}</div>}
                        <div className="mt-2 inline-flex items-center gap-1 text-[color:var(--alb-sun)] text-sm font-semibold">{t('ad.visit')} <ExternalLink className="h-3 w-3" /></div>
                    </div>
                </div>
            </button>
        </div>
    );
}
